import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router';
import { resolveMarketLanguage, useMarketConfig } from '@/entities/market';
import { useHasSession, useMe } from '@/entities/session';
import { ApiErrorCode, isApiError } from '@/shared/api';
import {
  changeUiLanguage,
  DEFAULT_SECTION,
  getUiLanguage,
  loginPath,
  RETURN_TO_PARAM,
  ROUTES,
  safeReturnPath,
} from '@/shared/config';
import { MapTilesProvider } from '@/shared/lib';
import { ContentLanguagesProvider, ErrorState, Skeleton } from '@/shared/ui';
import { AppShell } from '@/widgets/app-shell';

/** Полноэкранное состояние загрузки (до конфигурации рынка или профиля). */
function BootSkeleton() {
  const { t } = useTranslation('shell');
  return (
    <div
      aria-busy="true"
      className="flex h-screen min-h-760 w-full min-w-1280 overflow-hidden bg-paper-base"
    >
      <span className="sr-only">{t('boot.loading')}</span>
      <div className="flex w-240 shrink-0 flex-col gap-14 bg-sidebar px-16 pt-20 shadow-[inset_-1px_0_0_var(--vw-gold-soft)]">
        <Skeleton className="h-32 w-140" />
        <Skeleton className="h-200 w-full" />
      </div>
      <div className="flex flex-1 flex-col">
        <div className="h-54 border-b border-line-hair bg-paper-pure" />
        <div className="flex flex-col gap-14 px-24 pt-20">
          <Skeleton className="h-34 w-320" />
          <Skeleton className="h-320 w-full rounded-18" />
        </div>
      </div>
    </div>
  );
}

function FullScreenError({
  title,
  error,
  onRetry,
}: {
  title: string;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div className="flex min-h-screen min-w-1280 items-center justify-center bg-paper-deep px-24">
      <div className="w-480 rounded-20 border border-line-strong bg-paper-pure">
        <ErrorState title={title} error={error} onRetry={onRetry} />
      </div>
    </div>
  );
}

/**
 * Конфигурация рынка (GET /admin/config) нужна до входа: бренд, языки, валюта.
 * Язык интерфейса приводится к допустимому на рынке (RU — только ru; World — en/es).
 */
export function MarketGate() {
  const { t } = useTranslation('shell');
  const config = useMarketConfig();
  const data = config.data;

  useEffect(() => {
    if (!data) return;
    const language = resolveMarketLanguage(data, getUiLanguage());
    void changeUiLanguage(language, { persist: data.languages.length > 1 });
  }, [data]);

  if (config.isPending) return <BootSkeleton />;
  if (config.isError) {
    return (
      <FullScreenError
        title={t('boot.configError')}
        error={config.error}
        onRetry={() => void config.refetch()}
      />
    );
  }
  return <Outlet />;
}

/** Экран входа — только без сессии; с сессией — на исходный адрес или «Главную». */
export function GuestOnly() {
  const hasSession = useHasSession();
  const [searchParams] = useSearchParams();
  if (hasSession) {
    const target =
      safeReturnPath(searchParams.get(RETURN_TO_PARAM)) ?? ROUTES.section(DEFAULT_SECTION);
    return <Navigate to={target} replace />;
  }
  return <Outlet />;
}

/** Защищённая зона: без сессии — на вход с сохранением адреса; профиль `me` → оболочка. */
export function RequireAuth() {
  const { t } = useTranslation('shell');
  const location = useLocation();
  const hasSession = useHasSession();
  const config = useMarketConfig().data;
  const me = useMe({ enabled: hasSession });
  const profileLanguage = me.data?.member.admin_locale;

  // Язык админки — из профиля участника (TeamMember.admin_locale, ТЗ ACC 4.7.1), если он допустим на рынке.
  useEffect(() => {
    if (!config || !profileLanguage || !config.languages.includes(profileLanguage)) return;
    if (profileLanguage !== getUiLanguage()) void changeUiLanguage(profileLanguage);
  }, [config, profileLanguage]);

  if (!hasSession) {
    return <Navigate to={loginPath(`${location.pathname}${location.search}`)} replace />;
  }
  if (me.isPending || !config) return <BootSkeleton />;
  if (me.isError) {
    // 401 уже обработан интерсептором (выход и переход на вход).
    if (isApiError(me.error) && me.error.code === ApiErrorCode.Unauthorized)
      return <BootSkeleton />;
    return (
      <FullScreenError
        title={t('boot.meError')}
        error={me.error}
        onRetry={() => void me.refetch()}
      />
    );
  }
  // Языки контента для полей LocalizedText — параметры рынка, а не код рынка (ARCH-01).
  // Подложка карт — источники и порог переключения из `maps` конфигурации (BR-GEO-64).
  return (
    <ContentLanguagesProvider
      value={{ languages: config.languages, autoTranslate: config.content.translation }}
    >
      <MapTilesProvider
        value={{
          sources: config.maps.fallback_tiles,
          errorThreshold: config.maps.tile_error_threshold,
        }}
      >
        <AppShell
          brandName={config.brand.name}
          logoUrl={config.brand.logo_url}
          languages={config.languages}
          me={me.data}
        />
      </MapTilesProvider>
    </ContentLanguagesProvider>
  );
}
